import { CheckboxGroupInput, TextInput, required } from "react-admin";
import { useWatch } from "react-hook-form";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { colors, fontFamilyMono } from "../../data/tokens";
import {
  PERMISSIONS,
  PROFILE_PRIORITY_NOTE,
  ProfileChip,
  asPermissionList,
  permissionInfo,
} from "./userRoleMeta";

// Prévia ao vivo do perfil que o painel vai aplicar com as permissões
// marcadas — o mesmo cálculo do login/menu
const ProfilePreview = () => {
  const permissions = useWatch({ name: "permissions" });
  const list = asPermissionList(permissions);
  const unknown = list.filter((code) => !permissionInfo(code));
  return (
    <Box sx={{ marginTop: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: 13, color: colors.textStrong }}>
          Perfil resultante no painel:
        </Typography>
        <ProfileChip permissions={list} />
      </Box>
      <Typography sx={{ fontSize: 12, color: colors.textMuted, marginTop: 0.5 }}>
        {PROFILE_PRIORITY_NOTE}
      </Typography>
      {unknown.length > 0 && (
        // Códigos fora da lista conhecida não somem ao salvar
        <Typography sx={{ fontSize: 12, color: colors.textMuted, marginTop: 0.5 }}>
          Mantidas sem alteração (desconhecidas pelo painel):{" "}
          <Box component="span" sx={{ fontFamily: fontFamilyMono }}>
            {unknown.join(", ")}
          </Box>
        </Typography>
      )}
    </Box>
  );
};

const atLeastOnePermission = (value: unknown) =>
  asPermissionList(value).length > 0
    ? undefined
    : "Marque ao menos uma permissão";

// Campos comuns de criar/editar. O editor JSON antigo aceitava qualquer
// texto (um código digitado errado deixava a pessoa sem acesso); agora as
// permissões são marcadas numa lista, com o efeito de cada uma.
export const UserRoleFormFields = ({
  isEdit = false,
  nameLocked = false,
  userCount,
}: {
  isEdit?: boolean;
  nameLocked?: boolean;
  userCount?: number;
}) => (
  <Grid container columnSpacing={2} rowSpacing={0.5}>
    {isEdit && (
      <Grid item xs={12} md={6}>
        {/* Chave do registro: editável na versão antiga — trocá-la
            desligaria a regra de todos os usuários vinculados */}
        <TextInput source="id" label="ID" fullWidth disabled helperText={false} />
      </Grid>
    )}
    <Grid item xs={12} md={isEdit ? 6 : 12}>
      <TextInput
        source="name"
        label="Nome"
        fullWidth
        validate={required()}
        disabled={nameLocked}
        helperText={
          nameLocked
            ? "Nome usado pelo servidor (login, cadastro e acesso da equipe) — não pode ser alterado"
            : false
        }
      />
    </Grid>
    <Grid item xs={12}>
      {!!userCount && (
        <Alert severity="warning" sx={{ marginBottom: 1.5 }}>
          Esta regra tem {userCount.toLocaleString("pt-BR")} usuário(s): mudar as
          permissões muda o acesso de todos eles.
        </Alert>
      )}
      <CheckboxGroupInput
        source="permissions"
        label="Permissões"
        row={false}
        validate={atLeastOnePermission}
        format={(value: unknown) => asPermissionList(value)}
        choices={PERMISSIONS}
        optionText={(choice: any) => (
          <span>
            <strong>{choice.name}</strong>
            <Box component="span" sx={{ color: colors.textMuted, marginLeft: 1 }}>
              {choice.effect}
            </Box>
          </span>
        )}
        helperText={false}
      />
      <ProfilePreview />
    </Grid>
  </Grid>
);
